import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
  Input,
  Label,
  Radio,
  RadioGroup,
  Divider,
  Text,
  Badge,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  Filter20Regular,
  Filter20Filled,
  Globe20Regular,
  Briefcase20Regular,
  Folder20Regular,
  Star20Regular,
} from '@fluentui/react-icons';
import { useUiStore, useSearchFilters } from '@/stores/uiStore';
import { countActiveFilters } from '@/types/searchFilters';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import type { SearchScopeKind } from '@/types/domain';

const useStyles = makeStyles({
  surface: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    // Le volet Outlook descend à 320 px : le panneau doit tenir dedans sans
    // provoquer de défilement horizontal.
    width: '292px',
    maxWidth: 'calc(100vw - 24px)',
    padding: tokens.spacingVerticalS,
  },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  sectionLabel: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  field: { display: 'flex', flexDirection: 'column', gap: '2px' },
  // Deux dates sur une ligne : côte à côte elles se lisent comme une plage.
  dates: { display: 'flex', gap: tokens.spacingHorizontalXS, minWidth: 0 },
  dateField: { flex: 1, minWidth: 0 },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  iconBtn: { minWidth: '24px', width: '24px', height: '24px', padding: 0, position: 'relative' },
  filterActive: { color: tokens.colorBrandForeground1 },
  // Pastille du NOMBRE de filtres, sur le bouton lui-même : sans elle, un filtre
  // oublié dans un panneau replié explique des résultats vides sans qu'on voie
  // pourquoi.
  count: { position: 'absolute', top: '-4px', right: '-4px', pointerEvents: 'none' },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  scopeLabel: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
});

const SCOPE_LABELS: Record<SearchScopeKind, string> = {
  all: 'Partout',
  container: 'Ce projet',
  folder: 'Ce dossier',
  favorites: 'Mes favoris',
};

interface Props {
  /** Portée effective (choix explicite, sinon défaut contextuel). */
  effectiveScope: SearchScopeKind;
  canContainer: boolean;
  canFolder: boolean;
}

/**
 * Panneau des filtres de recherche : périmètre ET critères façon Outlook.
 *
 * UN SEUL ENDROIT pour tout ce qui restreint la recherche. Le périmètre vivait
 * déjà derrière ce bouton entonnoir ; y ajouter les critères ailleurs aurait
 * obligé l'utilisateur à chercher dans deux menus ce qui est une seule question :
 * « qu'est-ce que je cherche, et où ? ».
 *
 * Un `Popover` et non un `Menu` : un menu ne peut pas héberger de champs de
 * saisie (chaque touche y est interprétée comme un raccourci de navigation).
 *
 * ENREGISTREMENT IMMÉDIAT, sans bouton « Appliquer » : la requête est temporisée
 * de 200 ms (cf. useSearch), donc la frappe ne déclenche pas une requête par
 * caractère, et les résultats se réajustent sous les yeux — ce qui est la boucle
 * de rétroaction attendue d'un filtre.
 */
export function SearchFiltersPopover({ effectiveScope, canContainer, canFolder }: Props) {
  const styles = useStyles();
  const filters = useSearchFilters();
  const setFilters = useUiStore((s) => s.setSearchFilters);
  const clearFilters = useUiStore((s) => s.clearSearchFilters);
  const setScopeOverride = useUiStore((s) => s.setScopeOverride);

  const activeCount = countActiveFilters(filters);
  const isScoped = effectiveScope !== 'all';
  // Le bouton s'allume pour l'un OU l'autre : une portée restreinte et un filtre
  // posé limitent tous deux ce qu'on voit.
  const highlighted = isScoped || activeCount > 0;

  const title = [
    `Portée : ${SCOPE_LABELS[effectiveScope]}`,
    activeCount > 0 ? `${activeCount} filtre(s)` : null,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <Popover positioning="below-end" trapFocus>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          size="small"
          className={mergeClasses(styles.iconBtn, highlighted && styles.filterActive)}
          icon={highlighted ? <Filter20Filled /> : <Filter20Regular />}
          aria-label="Filtres de recherche"
          title={title}
        >
          {activeCount > 0 && (
            <Badge className={styles.count} size="tiny" appearance="filled" color="brand">
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div className={styles.section}>
          <Text className={styles.sectionLabel}>Où chercher</Text>
          <RadioGroup
            value={effectiveScope}
            onChange={(_e, d) => {
              setScopeOverride(d.value as SearchScopeKind);
              addBreadcrumb({ category: 'ui.click', message: 'search-scope', data: { scope: d.value } });
            }}
          >
            {/* Les icônes sont conservées depuis l'ancien menu : c'est à elles que
                l'œil s'est habitué pour distinguer les quatre portées. `Radio`
                n'a pas de prop `icon`, d'où le nœud passé en libellé. */}
            <Radio
              value="all"
              label={
                <span className={styles.scopeLabel}>
                  <Globe20Regular /> Partout
                </span>
              }
            />
            <Radio
              value="container"
              disabled={!canContainer}
              label={
                <span className={styles.scopeLabel}>
                  <Briefcase20Regular /> Ce projet
                </span>
              }
            />
            <Radio
              value="folder"
              disabled={!canFolder}
              label={
                <span className={styles.scopeLabel}>
                  <Folder20Regular /> Ce dossier
                </span>
              }
            />
            <Radio
              value="favorites"
              label={
                <span className={styles.scopeLabel}>
                  <Star20Regular /> Mes favoris
                </span>
              }
            />
          </RadioGroup>
        </div>

        <Divider />

        <div className={styles.section}>
          {/* Le titre de section porte seul l'information : « Filtrer les mails »
              dit déjà que ces critères ne concernent que les mails. La phrase
              explicative qui suivait détaillait la conséquence côté serveur (les
              projets et les dossiers sortent des résultats dès qu'un filtre est
              posé) ; elle occupait trois lignes en tête d'un panneau déjà long. */}
          <Text className={styles.sectionLabel}>Filtrer les mails</Text>

          <div className={styles.field}>
            <Label size="small" htmlFor="flt-from">
              De
            </Label>
            <Input
              id="flt-from"
              size="small"
              placeholder="Nom ou adresse de l'expéditeur"
              value={filters.from}
              onChange={(_e, d) => setFilters({ from: d.value })}
            />
          </div>

          <div className={styles.field}>
            <Label size="small" htmlFor="flt-to">
              À
            </Label>
            <Input
              id="flt-to"
              size="small"
              placeholder="Destinataire ou copie"
              value={filters.to}
              onChange={(_e, d) => setFilters({ to: d.value })}
            />
          </div>

          <div className={styles.field}>
            <Label size="small" htmlFor="flt-subject">
              Objet
            </Label>
            <Input
              id="flt-subject"
              size="small"
              placeholder="Mots de l'objet"
              value={filters.subject}
              onChange={(_e, d) => setFilters({ subject: d.value })}
            />
          </div>

          <div className={styles.field}>
            <Label size="small">Date d'envoi</Label>
            <div className={styles.dates}>
              {/* `type="date"` natif : le sélecteur de date de Fluent v9 n'est pas
                  dans les dépendances, et le natif est localisé par le système —
                  donc au format français dans Outlook. La borne de fin est INCLUSE
                  pour l'utilisateur ; la conversion en borne exclusive se fait
                  dans filtersToPayload. */}
              <Input
                className={styles.dateField}
                size="small"
                type="date"
                aria-label="Date de début"
                value={filters.dateFrom}
                max={filters.dateTo || undefined}
                onChange={(_e, d) => setFilters({ dateFrom: d.value })}
              />
              <Input
                className={styles.dateField}
                size="small"
                type="date"
                aria-label="Date de fin"
                value={filters.dateTo}
                min={filters.dateFrom || undefined}
                onChange={(_e, d) => setFilters({ dateTo: d.value })}
              />
            </div>
          </div>

          <div className={styles.field}>
            <Label size="small">Pièces jointes</Label>
            <RadioGroup
              layout="horizontal"
              value={
                filters.hasAttachments === null ? 'any' : filters.hasAttachments ? 'yes' : 'no'
              }
              onChange={(_e, d) =>
                setFilters({
                  hasAttachments: d.value === 'any' ? null : d.value === 'yes',
                })
              }
            >
              <Radio value="any" label="Indifférent" />
              <Radio value="yes" label="Avec" />
              <Radio value="no" label="Sans" />
            </RadioGroup>
          </div>

          <div className={styles.field}>
            <Label size="small" htmlFor="flt-attach">
              Nom de pièce jointe
            </Label>
            <Input
              id="flt-attach"
              size="small"
              placeholder="devis.pdf"
              value={filters.attachmentName}
              onChange={(_e, d) => setFilters({ attachmentName: d.value })}
            />
          </div>

          <div className={styles.field}>
            <Label size="small" htmlFor="flt-filedby">
              Rangé par
            </Label>
            <Input
              id="flt-filedby"
              size="small"
              placeholder="Nom de la personne"
              value={filters.filedBy}
              onChange={(_e, d) => setFilters({ filedBy: d.value })}
            />
          </div>
        </div>

        <div className={styles.actions}>
          <Text className={styles.hint}>
            {activeCount > 0 ? `${activeCount} filtre(s) actif(s)` : 'Aucun filtre'}
          </Text>
          <Button
            appearance="secondary"
            size="small"
            disabled={activeCount === 0}
            onClick={() => clearFilters()}
          >
            Effacer les filtres
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
}
